import type { MenuItem, PricingStrategy } from '../types';
import { calculateDisplayPrice, shouldShowBadge } from '../services/PricingMatrixService';

/** Whether the item's base price should be hidden from card/list views */
export function shouldHidePrice(item: MenuItem): boolean {
  return shouldShowBadge(item);
}

/**
 * Compute the lowest possible price from required option groups.
 * Returns the base price + the cheapest required option in each mandatory group.
 * @deprecated — use calculateDisplayPrice() from PricingMatrixService instead
 */
export function startingFromPrice(item: MenuItem): number | null {
  const dp = calculateDisplayPrice(item, 'LOWEST');
  return dp.price;
}

/**
 * Format the display label for an item's price on card/list views.
 * Now delegates to the Triple-Layer PricingMatrixService.
 * Returns { text, badge, prefix } where:
 *  - text: formatted price string (e.g. "QAR 8.00") or empty
 *  - badge: subtitle text for fallback (e.g. 'Customizable') or null
 *  - prefix: "From" | "Up to" | null — strategy hint for UI rendering
 */
export function formatCardPrice(
  item: MenuItem,
  currencySymbol: string,
  strategy: PricingStrategy = 'LOWEST',
): { text: string; badge: string | null; prefix: string | null } {
  if (!shouldHidePrice(item)) {
    return {
      text: `${currencySymbol} ${item.basePrice.toFixed(2)}`,
      badge: null,
      prefix: null,
    };
  }
  const dp = calculateDisplayPrice(item, strategy, currencySymbol);
  if (dp.price !== null) {
    const prefix = strategy === 'HIGHEST' ? 'Up to' : 'From';
    return {
      text: `${currencySymbol} ${dp.price.toFixed(2)}`,
      badge: null,
      prefix,
    };
  }
  return { text: '', badge: dp.label || 'Customizable', prefix: null };
}
